import * as React from 'react';
import styled, { css } from 'styled-components';
import { isEmpty } from 'lodash';
import {
  IConfirmationInstructions as ConfirmationInstructions,
  AccessType,
} from '@bb/domain/dist/types';
import { formatTimeStr, formatPhoneNumber } from 'utils';
import listingPlaceholder from '../../../img/listingPlaceholder.svg';

import { FALLBACK_LISTING_THUMBNAIL } from '../../constants';
import { BuyerAppointments } from './types';

type OtherInstructions =
  | 'lights'
  | 'shoes'
  | 'card'
  | 'lock_doors'
  | 'knockFirst'
  | 'callIfLate'
  | 'reco';

const accessMap: Record<
  Exclude<AccessType, '' | 'Supra Lockbox'>,
  keyof Partial<ConfirmationInstructions>
> = {
  'Lock Box': 'lockbox',
  Key: 'key',
  'Door Code': 'doorcode',
  Direct: 'direct',
  SentriLock: 'sentrilock',
  SentriLockV2: 'sentrilockv2',
  'SentriLock App': 'sentrilockapp',
  MasterLock: 'masterlock',
  'Supra eKEY': 'supraekey',
};

const getAccessLabel = (access: AccessType) => {
  switch (access) {
    case 'SentriLock':
      return 'SentriLock 1-Day Code';
    case 'SentriLockV2':
      return 'SentriLock';
    case 'Lock Box':
      return 'Combination Lock Box';
    case 'MasterLock':
      return 'Master Lock';
    default:
      return access;
  }
};

const instructionMap: Record<
  keyof Pick<ConfirmationInstructions, OtherInstructions>,
  string
> = {
  lights: 'Turn Off Lights',
  shoes: 'Remove Shoes',
  card: 'Leave Card',
  lock_doors: 'Lock Doors', // eslint-disable-line
  knockFirst: 'Knock First',
  callIfLate: 'Call if Late or Cancelling',
  reco: 'Bring Agent License',
};

const instructionsKeys: OtherInstructions[] = [
  'lights',
  'shoes',
  'card',
  'lock_doors',
  'knockFirst',
  'callIfLate',
  'reco',
];

const BuyerAppointmentDetail: React.FunctionComponent<{
  appointment: BuyerAppointments[number];
  showAccessInfo: boolean;
  hasSupraRebrandingFeature?: boolean;
}> = ({ appointment, showAccessInfo, hasSupraRebrandingFeature }) => {
  const {
    appointmentConfig,
    listingId,
    listing: { timezone },
    buyers,
    status,
    displayStatus,
  } = appointment;
  const { org } = listingId;

  const [listingAgent] = listingId.reps;
  const listingAgentBranch =
    listingAgent?.address &&
    listingAgent?.location &&
    org.addresses[Number(listingAgent.location)]?.[
      Number(listingAgent.address)
    ];

  const { access } = appointmentConfig;
  const accessInstruction =
    appointmentConfig.instructions[
      accessMap[access as Exclude<AccessType, '' | 'Supra Lockbox'>]
    ];

  const timeStartString = formatTimeStr(appointment.time, timezone);
  const timeEndString = formatTimeStr(appointment.timeEnd, timezone);
  const timeText = `${timeStartString} - ${timeEndString}`;

  const createBuyerComponent = (
    buyer: BuyerAppointments[number]['buyers'][number],
  ) => (
    <div>
      {
        <div>
          {buyer.firstName} {buyer.lastName}
        </div>
      }
      {buyer.email && <div>{buyer.email}</div>}
      {buyer.phone && <div>{formatPhoneNumber(buyer.phone)}</div>}
    </div>
  );

  return (
    <PDFAppointmentDetailContainer className="print-break">
      <AppointmentDetailDateTime>{timeText}</AppointmentDetailDateTime>
      <PDFAppointmentDetailContents>
        <AppointmentListingDetails>
          <AppointmentImageContainer>
            {listingId.thumbnail ? (
              <img
                className="img-responsive"
                src={listingId.thumbnail}
                alt={listingId.line1}
              />
            ) : (
              <img
                src={
                  hasSupraRebrandingFeature
                    ? listingPlaceholder
                    : FALLBACK_LISTING_THUMBNAIL
                }
                alt="Placeholder"
                className="img-responsive"
              />
            )}
          </AppointmentImageContainer>
          {`${listingId.line1}${listingId.line2 ? `, ${listingId.line2}` : ''}`}{' '}
          <br />
          {listingId.city}
          {!!listingId.province && `, ${listingId.province}`}
          {!!listingId.postal_code && `, ${listingId.postal_code}`}
          <br />${listingId.list_price.toLocaleString()}
        </AppointmentListingDetails>
        <AppointmentDetails>
          <StatusSection>
            <h5>Status</h5>
            {displayStatus}
          </StatusSection>
          {status === 'confirm' && (
            <>
              <Row>
                {showAccessInfo && (
                  <AccessSection>
                    <h5>Access</h5>
                    <ul className="p-l-md">
                      <li>
                        Access: {getAccessLabel(appointmentConfig.access)}
                      </li>
                      {access && !isEmpty(accessInstruction) && (
                        <>
                          {(accessInstruction as { code: string }).code && (
                            <li>
                              Code:{' '}
                              {(accessInstruction as { code: string }).code}
                            </li>
                          )}
                          {(accessInstruction as { details: string })
                            .details && (
                            <li>
                              {
                                (accessInstruction as { details: string })
                                  .details
                              }
                            </li>
                          )}
                        </>
                      )}
                    </ul>
                    {appointmentConfig.instructions.alarm &&
                      (appointmentConfig.instructions.alarm.code ||
                        appointmentConfig.instructions.alarm.details) && (
                        <div>
                          <h5>Alarm</h5>
                          <ul className="p-l-md">
                            {appointmentConfig.instructions.alarm.code && (
                              <li>
                                Code:{' '}
                                {appointmentConfig.instructions.alarm.code}
                              </li>
                            )}
                            {appointmentConfig.instructions.alarm.details && (
                              <li>
                                {appointmentConfig.instructions.alarm.details}
                              </li>
                            )}
                          </ul>
                        </div>
                      )}
                  </AccessSection>
                )}

                <OtherInstructionsSection>
                  {instructionsKeys.map(
                    (instruction: keyof typeof instructionMap, index) => {
                      if (appointmentConfig.instructions[instruction]) {
                        return (
                          <div key={index}>
                            <i className="fa fa-check-circle"></i>{' '}
                            {instructionMap[instruction]}
                          </div>
                        );
                      }

                      return null;
                    },
                  )}
                </OtherInstructionsSection>
              </Row>
              <Linebreak />
            </>
          )}
          <Row>
            {!!listingAgent && (
              <ListingAgentSection>
                <h5>Listing Agent</h5>
                {`${listingAgent.firstName} ${listingAgent.lastName}`}
                <div>
                  {(listingAgentBranch && listingAgentBranch.name) || org.name}
                </div>
                {listingAgent.phone && (
                  <div>M. {formatPhoneNumber(listingAgent.phone)}</div>
                )}
                {listingAgentBranch && (
                  <div>O. {formatPhoneNumber(listingAgentBranch.phone)}</div>
                )}
                <div>{listingAgent.email}</div>
              </ListingAgentSection>
            )}
            {Boolean(buyers?.length) && (
              <BuyersSection>
                <h5>Buyer(s)</h5>
                {buyers.map(createBuyerComponent)}
              </BuyersSection>
            )}
          </Row>
        </AppointmentDetails>
      </PDFAppointmentDetailContents>
    </PDFAppointmentDetailContainer>
  );
};

const AppointmentDetailDateTime = styled.h5`
  margin-top: 5px;
  padding-bottom: 10px;
  border-bottom: solid 1px #ddd;
  text-align: center;
`;

const FlexCol = css`
  display: flex;
  flex-direction: column;
`;

const PDFAppointmentDetailContainer = styled.div`
  ${FlexCol}
  margin: 10px auto;
  padding: 5px 20px;
  width: 95%;
  border: solid 1px #ccc;
  font-size: 11px;
  h5 {
    font-size: 13px;
  }
`;

const AppointmentImageContainer = styled.div`
  width: 75%;
  img {
    border: solid 1px #ddd;
  }
`;

const Linebreak = styled.div`
  border-top: 1px solid #e5e5e5;
  padding-top: 10px;
`;

const Row = styled.div`
  display: flex;
  margin-bottom: 10px;
`;

const PDFAppointmentDetailContents = styled.div`
  display: flex;
`;

const AppointmentListingDetails = styled.div`
  ${FlexCol}
  width: 25%;
`;

const AppointmentDetails = styled.div`
  ${FlexCol}
  width: 75%;
`;

const HalfWidth = styled.div`
  width: 50%;
`;

const OtherInstructionsSection = styled(HalfWidth)`
  ${FlexCol}
  justify-content: center;
  padding-left: 5px;
`;

const BuyersSection = styled(HalfWidth)`
  & > div {
    margin-bottom: 12px;
  }
`;

const StatusSection = styled.p`
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e5e5;
`;

const AccessSection = styled(HalfWidth)``;

const ListingAgentSection = styled(HalfWidth)``;

export default BuyerAppointmentDetail;
